/**
 * JKL Pick Your Shade
 * 
 * Two-step shade finder wizard for makeup products.
 * Migrated from Hydrogen PickYourShades component.
 * 
 * Features:
 * - Step 1: Intensity selection via color circles
 * - Step 2: Complexion selection via images
 * - Dynamic background color based on selection
 * - Result view with recommended product and Add to Cart
 * - Mobile-responsive with scroll behavior
 */

(function() {
  'use strict';

  // Find all shade finder sections on the page
  const sections = document.querySelectorAll('[data-section-id]');
  
  sections.forEach(function(section) {
    if (!section.classList.contains('jkl-pys')) return;
    
    initShadeFinder(section);
  });

  /**
   * Initialize a single shade finder instance
   * @param {HTMLElement} section - The section element
   */
  function initShadeFinder(section) {
    // Get configuration from JSON
    const configEl = section.querySelector('[data-shade-config]');
    if (!configEl) {
      console.error('JKL Pick Your Shade: Configuration not found');
      return;
    }

    function decodeHtml(str) {
      if (typeof str !== 'string') return str;
      var el = document.createElement('div');
      el.innerHTML = str;
      return el.textContent || el.innerText || str;
    }

    let config;
    try {
      config = JSON.parse(configEl.textContent);
      Object.keys(config.translations).forEach(function(key) {
        config.translations[key] = decodeHtml(config.translations[key]);
      });
      // Debug: Log configuration
      console.log('JKL Pick Your Shade: Configuration loaded', config);
      console.log('JKL Pick Your Shade: Shades count:', config.shades.length);
      config.shades.forEach(function(shade, index) {
        console.log('Shade ' + index + ':', shade.title, '- innerItems:', shade.innerItems ? shade.innerItems.length : 0);
      });
    } catch (e) {
      console.error('JKL Pick Your Shade: Invalid configuration JSON', e);
      console.error('Raw JSON:', configEl.textContent);
      return;
    }

    // State
    const state = {
      currentStep: 'intensity', // 'intensity' or 'complexion'
      selectedIntensity: null,
      selectedComplexion: null,
      result: null
    };

    // DOM Elements
    const elements = {
      wizard: section.querySelector('[data-wizard]'),
      result: section.querySelector('[data-result]'),
      imageArea: section.querySelector('[data-image-area]'),
      mainImage: section.querySelector('[data-main-image]'),
      intensityLabel: section.querySelector('[data-intensity-label]'),
      intensityValue: section.querySelector('[data-intensity-value]'),
      stepIntensity: section.querySelector('[data-step="intensity"]'),
      stepComplexion: section.querySelector('[data-step="complexion"]'),
      complexionPickers: section.querySelector('[data-complexion-pickers]'),
      stepSpan: section.querySelector('[data-step-span]'),
      stepContent: section.querySelector('[data-step-content]'),
      ballonTitle: section.querySelector('[data-ballon-title]'),
      ballonDescription: section.querySelector('[data-ballon-description]'),
      btnBack: section.querySelector('[data-btn-back]'),
      btnNext: section.querySelector('[data-btn-next]'),
      btnRestart: section.querySelector('[data-btn-restart]'),
      // Result elements
      resultIntensityImg: section.querySelector('[data-result-intensity-img]'),
      resultIntensityValue: section.querySelector('[data-result-intensity-value]'),
      resultComplexionImg: section.querySelector('[data-result-complexion-img]'),
      resultComplexionValue: section.querySelector('[data-result-complexion-value]'),
      resultProductImg: section.querySelector('[data-result-product-img]'),
      resultShadeValue: section.querySelector('[data-result-shade-value]'),
      resultVariantId: section.querySelector('[data-result-variant-id]')
    };

    // Build shade lookup for quick access
    const shadeMap = {};
    config.shades.forEach(function(shade) {
      shadeMap[shade.id] = shade;
      if (shade.innerItems && shade.innerItems.length > 0) {
        shade.innerItems.forEach(function(inner) {
          if (inner && inner.id) {
            shadeMap[inner.id] = inner;
          }
        });
      }
    });
    
    console.log('JKL Pick Your Shade: ShadeMap built', Object.keys(shadeMap).length, 'entries');

    // Initialize event listeners
    initEventListeners();

    /**
     * Initialize all event listeners
     */
    function initEventListeners() {
      // Intensity picker clicks
      const intensityPickers = section.querySelectorAll('[data-intensity-picker]');
      intensityPickers.forEach(function(picker) {
        picker.addEventListener('click', function() {
          const shadeId = picker.dataset.shadeId;
          selectIntensity(shadeId);
        });
      });

      // Navigation buttons
      if (elements.btnBack) {
        elements.btnBack.addEventListener('click', goBack);
      }
      if (elements.btnNext) {
        elements.btnNext.addEventListener('click', goNext);
      }
      if (elements.btnRestart) {
        elements.btnRestart.addEventListener('click', restart);
      }
    }

    /**
     * Select an intensity shade
     * @param {string} shadeId - The shade ID
     */
    function selectIntensity(shadeId) {
      const shade = shadeMap[shadeId];
      if (!shade) return;

      state.selectedIntensity = shadeId;

      // Update visual selection
      const pickers = section.querySelectorAll('[data-intensity-picker]');
      pickers.forEach(function(p) {
        p.classList.toggle('is-selected', p.dataset.shadeId === shadeId);
      });

      // Update radio input
      const input = section.querySelector('input[name="intensity"][value="' + shadeId + '"]');
      if (input) input.checked = true;

      // Update background color
      if (shade.backgroundColor) {
        elements.imageArea.style.backgroundColor = shade.backgroundColor;
      }

      // Update image
      if (shade.image) {
        elements.mainImage.src = shade.image;
      }

      // Update intensity label
      if (elements.intensityLabel) {
        elements.intensityLabel.style.display = 'block';
        elements.intensityValue.textContent = shade.title;
      }

      // Update step description
      updateStepDescription('intensity');

      // Update ballon text
      updateBallonText(shade.title, shade.description);

      // Pre-select first complexion
      if (shade.innerItems && shade.innerItems.length > 0) {
        state.selectedComplexion = shade.innerItems[0].id;
      }

      // Show next button
      if (elements.btnNext) {
        elements.btnNext.style.display = '';
      }

      // Scroll to image on mobile
      scrollToImageOnMobile();
    }

    /**
     * Select a complexion shade
     * @param {string} shadeId - The shade ID
     */
    function selectComplexion(shadeId) {
      const intensityShade = shadeMap[state.selectedIntensity];
      if (!intensityShade) return;

      const complexion = intensityShade.innerItems.find(function(item) {
        return item.id === shadeId;
      });
      if (!complexion) return;

      state.selectedComplexion = shadeId;

      // Update visual selection
      const pickers = section.querySelectorAll('[data-complexion-picker]');
      pickers.forEach(function(p) {
        p.classList.toggle('is-selected', p.dataset.shadeId === shadeId);
      });

      // Update image
      if (complexion.image) {
        elements.mainImage.src = complexion.image;
      }

      // Update ballon text with combined title
      const combinedTitle = intensityShade.title + ' - ' + complexion.title;
      updateBallonText(combinedTitle, complexion.description);

      // Scroll to image on mobile
      scrollToImageOnMobile();
    }

    /**
     * Go to the next step or submit
     */
    function goNext() {
      if (state.currentStep === 'intensity') {
        // Move to complexion step
        goToComplexionStep();
      } else if (state.currentStep === 'complexion') {
        // Submit and show result
        showResult();
      }
    }

    /**
     * Go back to the previous step
     */
    function goBack() {
      if (state.currentStep === 'complexion') {
        goToIntensityStep();
      }
    }

    /**
     * Go to intensity step
     */
    function goToIntensityStep() {
      state.currentStep = 'intensity';

      // Show/hide steps
      elements.stepIntensity.classList.add('is-active');
      elements.stepIntensity.style.display = '';
      elements.stepComplexion.classList.remove('is-active');
      elements.stepComplexion.style.display = 'none';

      // Update description
      updateStepDescription('intensity');

      // Hide back button
      if (elements.btnBack) {
        elements.btnBack.style.display = 'none';
      }

      // Update ballon with intensity data
      const shade = shadeMap[state.selectedIntensity];
      if (shade) {
        updateBallonText(shade.title, shade.description);
        if (shade.image) {
          elements.mainImage.src = shade.image;
        }
      }

      // Scroll to image on mobile
      scrollToImageOnMobile();
    }

    /**
     * Go to complexion step
     */
    function goToComplexionStep() {
      if (!state.selectedIntensity) {
        console.log('JKL Pick Your Shade: No intensity selected');
        return;
      }

      state.currentStep = 'complexion';
      const intensityShade = shadeMap[state.selectedIntensity];
      
      // Debug logging
      console.log('JKL Pick Your Shade: Going to complexion step');
      console.log('Selected intensity ID:', state.selectedIntensity);
      console.log('Intensity shade:', intensityShade);
      console.log('Inner items:', intensityShade ? intensityShade.innerItems : 'N/A');

      // Build complexion pickers
      buildComplexionPickers(intensityShade);

      // Show/hide steps
      elements.stepIntensity.classList.remove('is-active');
      elements.stepIntensity.style.display = 'none';
      elements.stepComplexion.classList.add('is-active');
      elements.stepComplexion.style.display = '';

      // Update description
      updateStepDescription('complexion');

      // Show back button
      if (elements.btnBack) {
        elements.btnBack.style.display = '';
      }

      // Select first complexion by default
      if (intensityShade.innerItems && intensityShade.innerItems.length > 0) {
        selectComplexion(intensityShade.innerItems[0].id);
      }

      // Scroll to image on mobile
      scrollToImageOnMobile();
    }

    /**
     * Build complexion picker elements
     * @param {Object} intensityShade - The selected intensity shade
     */
    function buildComplexionPickers(intensityShade) {
      if (!intensityShade.innerItems || !elements.complexionPickers) return;

      // Clear existing pickers
      elements.complexionPickers.innerHTML = '';

      intensityShade.innerItems.forEach(function(item) {
        const label = document.createElement('label');
        label.className = 'jkl-pys__picker jkl-pys__picker--image';
        label.setAttribute('aria-labelledby', 'shade-complexion-' + item.id);

        const circle = document.createElement('div');
        circle.className = 'jkl-pys__picker-circle jkl-pys__picker-circle--image';
        circle.dataset.complexionPicker = '';
        circle.dataset.shadeId = item.id;

        const img = document.createElement('img');
        img.src = item.image;
        img.alt = item.title;

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'complexion';
        input.value = item.id;
        input.className = 'sr-only';

        const title = document.createElement('div');
        title.id = 'shade-complexion-' + item.id;
        title.className = 'jkl-pys__picker-title';
        title.textContent = item.title;

        circle.appendChild(img);
        circle.appendChild(input);
        label.appendChild(circle);
        label.appendChild(title);
        elements.complexionPickers.appendChild(label);

        // Add click listener
        circle.addEventListener('click', function() {
          selectComplexion(item.id);
        });
      });
    }

    /**
     * Update step description text
     * @param {string} step - Current step ('intro', 'intensity', 'complexion')
     */
    function updateStepDescription(step) {
      const t = config.translations;

      if (step === 'intro' || !state.selectedIntensity) {
        elements.stepSpan.textContent = t.descriptionIntroSpan;
        elements.stepContent.textContent = t.descriptionIntroContent;
      } else if (step === 'intensity') {
        elements.stepSpan.textContent = t.descriptionIntensitySpan;
        elements.stepContent.textContent = '';
      } else if (step === 'complexion') {
        elements.stepSpan.textContent = t.descriptionComplexionSpan;
        // Parse the complexion content with bold formatting
        const parts = t.descriptionComplexionContent.split('|');
        if (parts.length === 3) {
          elements.stepContent.innerHTML = parts[0] + ', <span class="jkl-pys__step-content-bold">' + parts[1] + '</span>, ' + parts[2] + '.';
        } else {
          elements.stepContent.textContent = t.descriptionComplexionContent;
        }
      }
    }

    /**
     * Update ballon text
     * @param {string} title - The shade title
     * @param {string} description - The shade description
     */
    function updateBallonText(title, description) {
      const t = config.translations;

      if (title) {
        // Parse ballon text template: "L'INTENSITÀ | {{ shade }}"
        const parts = t.ballonTextTemplate.split('|');
        if (parts.length >= 2) {
          elements.ballonTitle.innerHTML = parts[0].trim() + ' <span class="jkl-pys__ballon-title-bold">' + title + '</span>' + (parts[2] ? parts[2].trim() : '');
        } else {
          elements.ballonTitle.innerHTML = '<span class="jkl-pys__ballon-title-bold">' + title + '</span>';
        }
      } else {
        elements.ballonTitle.textContent = t.noShadeText;
      }

      // Use description if provided, otherwise use default
      elements.ballonDescription.textContent = description || t.ballonDescription;
    }

    /**
     * Normalize variant title for comparison
     * Converts to lowercase and replaces spaces with underscores
     */
    function normalizeVariantTitle(title) {
      if (!title) return '';
      return title.toLowerCase().replace(/\s+/g, '_');
    }

    /**
     * Find variant by title with flexible matching
     */
    function findVariantByTitle(variants, title) {
      if (!variants || !variants.length || !title) return null;
      
      // Try exact match first
      var exactMatch = variants.find(function(v) {
        return v.title === title;
      });
      if (exactMatch) return exactMatch;
      
      // Try normalized match (case-insensitive, space/underscore flexible)
      var normalizedTitle = normalizeVariantTitle(title);
      return variants.find(function(v) {
        return normalizeVariantTitle(v.title) === normalizedTitle;
      });
    }

    /**
     * Show the result view
     */
    function showResult() {
      const intensityShade = shadeMap[state.selectedIntensity];
      const complexionShade = intensityShade.innerItems.find(function(item) {
        return item.id === state.selectedComplexion;
      });

      if (!intensityShade || !complexionShade) return;

      state.result = {
        intensity: state.selectedIntensity,
        complexion: state.selectedComplexion
      };

      // Update result view elements
      elements.resultIntensityImg.src = intensityShade.image;
      elements.resultIntensityImg.alt = intensityShade.title;
      elements.resultIntensityValue.textContent = intensityShade.title;

      elements.resultComplexionImg.src = complexionShade.image;
      elements.resultComplexionImg.alt = complexionShade.title;
      elements.resultComplexionValue.textContent = complexionShade.title;

      elements.resultShadeValue.textContent = complexionShade.title;

      // Find the correct variant from the product
      var variantId = null;
      var productImage = '';
      
      if (complexionShade.product && complexionShade.product.variants) {
        // Find variant by title
        var variant = findVariantByTitle(
          complexionShade.product.variants, 
          complexionShade.variantTitle || complexionShade.title
        );
        
        if (variant) {
          variantId = variant.id;
          productImage = variant.image || '';
        } else if (complexionShade.product.variants.length > 0) {
          // Fallback to first variant
          variantId = complexionShade.product.variants[0].id;
          productImage = complexionShade.product.variants[0].image || '';
        }
      }
      
      // Fallback to main product if no specific product selected
      if (!variantId && config.product && config.product.variants && config.product.variants.length > 0) {
        variantId = config.product.variants[0].id;
        productImage = config.product.variants[0].image || '';
      }

      elements.resultProductImg.src = productImage;
      elements.resultProductImg.alt = complexionShade.variantTitle || complexionShade.title;

      // Set variant ID for add to cart
      if (variantId) {
        elements.resultVariantId.value = variantId;
      }

      // Show result, hide wizard
      elements.wizard.style.display = 'none';
      elements.result.style.display = '';

      // Scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }

    /**
     * Restart the shade finder
     */
    function restart() {
      // Reset state
      state.currentStep = 'intensity';
      state.selectedIntensity = null;
      state.selectedComplexion = null;
      state.result = null;

      // Reset UI
      elements.wizard.style.display = '';
      elements.result.style.display = 'none';

      // Reset image area
      elements.imageArea.style.backgroundColor = '';
      elements.mainImage.src = config.mainImage;
      if (elements.intensityLabel) {
        elements.intensityLabel.style.display = 'none';
      }

      // Reset steps
      elements.stepIntensity.classList.add('is-active');
      elements.stepIntensity.style.display = '';
      elements.stepComplexion.classList.remove('is-active');
      elements.stepComplexion.style.display = 'none';

      // Reset pickers
      const pickers = section.querySelectorAll('[data-intensity-picker]');
      pickers.forEach(function(p) {
        p.classList.remove('is-selected');
      });

      // Reset buttons
      if (elements.btnBack) {
        elements.btnBack.style.display = 'none';
      }
      if (elements.btnNext) {
        elements.btnNext.style.display = 'none';
      }

      // Reset description and ballon
      updateStepDescription('intro');
      updateBallonText(null, null);
    }

    /**
     * Scroll to image area on mobile
     */
    function scrollToImageOnMobile() {
      // Only scroll on mobile (< 1024px)
      if (window.innerWidth < 1024 && elements.imageArea) {
        elements.imageArea.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }

    /**
     * Initialize add to cart form with AJAX submission
     */
    function initAddToCartForm() {
      const form = section.querySelector('[data-result-form]');
      if (!form) return;

      form.addEventListener('submit', function(e) {
        e.preventDefault();

        const variantId = form.querySelector('[data-result-variant-id]')?.value;
        if (!variantId) {
          console.error('JKL Pick Your Shade: No variant ID found');
          return;
        }

        const btn = form.querySelector('button[type="submit"]');
        if (btn) {
          btn.disabled = true;
          btn.classList.add('is-loading');
        }

        // Add to cart via AJAX
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId, 10),
              quantity: 1
            }]
          })
        })
        .then(function(response) { return response.json(); })
        .then(function(data) {
          if (data.status) {
            // Error
            console.error('JKL Pick Your Shade: Add to cart error:', data);
            return;
          }

          // Success - show toast notification
          if (window.JKLCartToast) {
            const addedItem = data.items ? data.items[0] : null;
            if (addedItem) {
              window.JKLCartToast.showWithProduct({
                title: addedItem.product_title,
                variant: addedItem.variant_title,
                price: addedItem.final_line_price,
                image: addedItem.image
              });
            } else {
              window.JKLCartToast.show();
            }
            // Update cart count in header
            if (window.JKLCartToast.updateCartCount) {
              window.JKLCartToast.updateCartCount(data.item_count);
            }
          }
        })
        .catch(function(err) {
          console.error('JKL Pick Your Shade: Add to cart failed:', err);
        })
        .finally(function() {
          if (btn) {
            btn.disabled = false;
            btn.classList.remove('is-loading');
          }
        });
      });
    }

    // Initialize add to cart form
    initAddToCartForm();
  }
})();
